import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import type { OrgThemePreset } from '../../../types';
import { cn } from '@/lib/utils';
import { THEME_PRESETS, THEME_PRESET_IDS, isOliseThemePreset, presetSwatches } from '@/lib/themePresets';

const fontsOf = (preset: OrgThemePreset): string | null => {
  if (!isOliseThemePreset(preset)) return null;
  const { fontSans, fontDisplay } = THEME_PRESETS[preset];
  return fontDisplay === fontSans ? fontSans : `${fontDisplay} + ${fontSans}`;
};

interface ThemeCardProps {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  swatches: readonly string[];
  label: string;
  description: string;
  footnote?: string | null;
}

/** One choice of the theme grid (a preset, or the custom colours). */
export const ThemeCard: React.FC<ThemeCardProps> = ({
  selected,
  disabled,
  onSelect,
  swatches,
  label,
  description,
  footnote,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={() => !selected && onSelect()}
    className={cn(
      'rounded-md border p-3 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'
    )}
  >
    <span className="flex items-center gap-1" aria-hidden="true">
      {swatches.map((color, index) => (
        <span
          key={`${color}-${index}`}
          className="h-4 w-4 rounded-full border border-border"
          style={{ backgroundColor: color }}
        />
      ))}
    </span>
    <span className="mt-2 flex items-center gap-2 font-medium">
      {label}
      {selected && <Check className="h-4 w-4 text-primary" />}
    </span>
    <span className="mt-1 block text-xs text-muted-foreground">{description}</span>
    {footnote && <span className="mt-1 block text-[11px] text-muted-foreground/80">{footnote}</span>}
  </button>
);

interface ThemePresetPickerProps {
  /** The selected preset; null when another choice (the custom card) is selected. */
  value: OrgThemePreset | null;
  disabled: boolean;
  onSelect: (preset: OrgThemePreset) => void;
  /** Extra choices rendered after the presets, in the same grid. */
  children?: React.ReactNode;
}

export const ThemePresetPicker: React.FC<ThemePresetPickerProps> = ({ value, disabled, onSelect, children }) => {
  const { t } = useTranslation();
  return (
    <div
      className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
      role="radiogroup"
      aria-label={t('orgExperience.theme.label')}
    >
      {THEME_PRESET_IDS.map((preset) => (
        <ThemeCard
          key={preset}
          selected={value === preset}
          disabled={disabled}
          onSelect={() => onSelect(preset)}
          swatches={presetSwatches(preset)}
          label={t(`orgExperience.theme.presets.${preset}.label`)}
          description={t(`orgExperience.theme.presets.${preset}.description`)}
          footnote={fontsOf(preset)}
        />
      ))}
      {children}
    </div>
  );
};

export default ThemePresetPicker;
